/**
 * Steps shared by the field validators that hold a NEW value to a rule without
 * blocking a document saved before the rule existed: the tenant's Marketing
 * Tag ids (`marketing-tag-ids.ts`), a Calendly Form's event link
 * (`calendly/validate-event-type-url.ts`), the slots that refuse a booking
 * Form (`field-form-relationship.ts`) and the tenant's own-document picks
 * (`Tenants/validate-own-tenant-relationship.ts`). Each relaxes its own rule
 * while a version is restored and when the save leaves the stored value
 * unchanged, compared as that field reads it. What it still checks then is its
 * own: the Tenants pick still refuses a malformed id or a failed lookup, and
 * the booking-Form slots still run Payload's relationship check first.
 */

import type { PayloadRequest } from 'payload';

/** The one part of the request `isRestoringVersion` reads, so a test needs no whole request. */
export type VersionRestoreRequest = Partial<Pick<PayloadRequest, 'context'>>;

/**
 * True while Payload's `restoreVersion` operation runs: it sets the flag on
 * the request context for the whole restore. A restored version's data comes
 * from the stored version, not the request, and Payload validates it against
 * the CURRENT document, so a version saved before a rule existed could never
 * be restored if the rule held it.
 */
export const isRestoringVersion = (req: VersionRestoreRequest | undefined): boolean =>
  req?.context?.isRestoringVersion === true;

/**
 * What a write can send for a text field: text, or a JSON number from a REST
 * write, which Mongoose would store as its digits. A validator decides what a
 * number means for its field.
 */
export type SubmittedText = string | number | null | undefined;

/**
 * A submitted value as text: `''` when there is none, `null` when it is not
 * text at all (a number, or any other JSON a REST write sent), for the
 * validator to refuse.
 */
export const submittedText = (value: SubmittedText): string | null => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : null;
};

/**
 * A stored value as text, `''` when it is none or not text. Payload passes the
 * stored value to a validator as it finds it (`lean` reads, no casting), so
 * one written outside Payload may be anything, and reading it must never
 * throw: on save Payload does not catch that, and every save of the document
 * would fail with a server error instead of a field message.
 */
export const storedText = (stored: unknown): string => (typeof stored === 'string' ? stored : '');
